import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Card,
  CardContent,
  useMediaQuery,
  FormControl,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';
import { AssignmentTurnedInOutlined, Factory, Pending, LocalShipping } from '@mui/icons-material';
import Grid from '@mui/material/Grid2';
import { getProductionTrackingAction } from "../../store/actions"
import { useDispatch, useSelector } from 'react-redux';

const tableConfigs = {
  pending: ['Product', 'Name', 'Order ID', 'Order Date', 'Status'],
  progress: ['Product', 'Name', 'Order ID', 'Order Date', 'Start Date', 'Estimated Days', 'Alert'],
  manufactured: ['Product', 'Name', 'Order ID', 'Order Date', 'Start Date', 'End Date', 'Alert'],
  tested: ['Product', 'Name', 'Order ID', 'Manufactured', 'Tested Date'],
  delivered: ['Product', 'Name', 'Order ID', 'Order Date', 'Delivered Date']
};

const DashboardCard = ({ title, value, Icon, color, delay, onCardClick, isActive }) => {
  const theme = useTheme();

  const handleClick = () => {
    if (typeof onCardClick === 'function') {
      const columns = tableConfigs[title.toLowerCase()];
      onCardClick(title, columns);
    }
  };
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}>
      <motion.div whileHover={{ scale: 1.05 }} transition={{ duration: 0.3 }} style={{ borderRadius: 8, overflow: 'hidden' }}>
        <Paper
          onClick={handleClick}
          sx={{
            p: 3,
            borderRadius: 5,
            cursor: 'pointer',
            transition: 'all 0.5s',
            background: isActive ? '#2D60FF' : '#EAEAEA',
            color: isActive ? 'white' : 'inherit',
            position: 'relative',
            overflow: 'hidden',
            '&:before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '200%',
              height: '100%',
              background: 'linear-gradient(90deg, #1814F3 0%, #1814F3 100%)',
              zIndex: 0,
              transform: 'translateX(-100%)',
              transition: 'transform 0.5s ease-in-out',
            },
            '&:hover:before': { transform: 'translateX(0)' },
            '&:hover *': { color: 'white !important' },
            '& *': { position: 'relative', zIndex: 1 },
          }}
        >
          <Box display="flex" alignItems="center">
            <Box sx={{ p: 1.5, borderRadius: 15, backgroundColor: color, transition: 'transform 0.2s' }}>
              <Icon style={{ height: 24, width: 24, color: 'white' }} />
            </Box>
            <Box ml={2}>
              <Typography variant="body2" sx={{ color: isActive ? 'white' : '#718EBF', fontFamily: 'Inter' }}>{title}</Typography>
              <Typography variant="h5" fontWeight="600" color="textPrimary">{value}</Typography>
            </Box>
          </Box>
        </Paper>
      </motion.div>
    </motion.div>
  );
};

const ProductionTracking = () => {
  const dispatch = useDispatch();
  const [selectedCard, setSelectedCard] = useState('Pending');
  const [tableColumns, setTableColumns] = useState(tableConfigs.pending);
  const [currentData, setCurrentData] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { successData } = useSelector((state) => state.getProductionTrackingReducer);

  // Add this useEffect to dispatch the action when component mounts
  useEffect(() => {
    dispatch(getProductionTrackingAction());
  }, [dispatch]);

  // Update current data when successData or selectedCard changes
  useEffect(() => {
    if (successData && successData.data) {
      const category = selectedCard.toLowerCase();
      setCurrentData(successData.data[category] || []);
    }
  }, [successData, selectedCard]);

  const handleCardClick = (title, columns) => {
    setSelectedCard(title);
    setTableColumns(columns);

    // Update current data based on selected category
    if (successData && successData.data) {
      const category = title.toLowerCase();
      setCurrentData(successData.data[category] || []);
    }
  };

  const handleFilterChange = (event) => {
    const category = event.target.value;
    const title = category.charAt(0).toUpperCase() + category.slice(1);
    setSelectedCard(title);
    setTableColumns(tableConfigs[category]);
  };

  // Get counts for each category
  const getCategoryCount = (category) => {
    if (successData && successData.data && successData.data[category.toLowerCase()]) {
      return successData.data[category.toLowerCase()].length;
    }
    return 0;
  };

  // Function to render table cell content based on column name and data
  const renderCellContent = (item, columnName) => {
    const formatDate = (dateString) => {
      if (!dateString) return '-';
      try {
        return new Date(dateString).toISOString().split('T')[0];
      } catch (error) {
        return '-';
      }
    };

    const calculateAlert = () => {
      switch (selectedCard.toLowerCase()) {
        case 'pending':
          return { text: 'Pending', color: '#FF0000' };
        case 'progress':
          return { text: 'In Progress', color: '#FFA500' };
        case 'manufactured':
          return { text: 'Manufactured', color: '#FFD700' };
        case 'tested':
          return { text: 'Tested', color: '#008000' };
        case 'delivered':
          return { text: 'Delivered', color: '#0000FF' };
        default:
          return { text: 'Unknown', color: '#000000' };
      }
    };

    switch (columnName) {
      case 'Product':
        return item.Machine?.name || '-';
      case 'Name':
        return item.client_name || '-';
      case 'Order ID':
        return item.id || '-';
      case 'Order Date':
        return formatDate(item.createdAt);
      case 'Status':
        const alertInfo = calculateAlert();
        return (
          <Box
            sx={{
              border: `1px solid ${alertInfo.color}`,
              padding: '6px 10px',
              borderRadius: '50px',
              display: 'inline-block',
              fontWeight: 'medium',
              color: alertInfo.color,
            }}
          >
            {item.status || alertInfo.text}
          </Box>
        );
      case 'Start Date':
        return formatDate(item.start_date);
      case 'Estimated Days':
        return item.Machine?.edp || '-';
      case 'End Date':
        return formatDate(item.end_date);
      case 'Alert':
        const alertInfo2 = calculateAlert();
        return (
          <Box
            sx={{
              border: `1px solid ${alertInfo2.color}`,
              padding: '6px 10px',
              borderRadius: '50px',
              display: 'inline-block',
              fontWeight: 'medium',
              color: alertInfo2.color,
            }}
          >
            {alertInfo2.text}
          </Box>
        );
      case 'Manufactured':
        return formatDate(item.end_date);
      case 'Tested Date':
        return formatDate(item.updatedAt);
      case 'Delivered Date':
        return formatDate(item.updatedAt);
      default:
        return '-';
    }
  };

  // New component for mobile card view
  const DataCard = ({ item, columns }) => {
    return (
      <Card sx={{
        mb: 2,
        borderRadius: 2,
        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
        overflow: 'visible'
      }}>
        <CardContent>
          {columns.map((col, index) => (
            <Box key={index} sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 0.75,
              borderBottom: index < columns.length - 1 ? '1px solid #E6EFF5' : 'none'
            }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#718EBF', fontFamily: 'Inter' }}>
                {col}
              </Typography>
              <Box>
                {renderCellContent(item, col)}
              </Box>
            </Box>
          ))}
        </CardContent>
      </Card>
    );
  };

  const getEmptyStateMessage = (cardType) => {
    switch (cardType.toLowerCase()) {
      case 'pending':
        return 'No pending orders available';
      case 'progress':
        return 'No orders currently in progress';
      case 'manufactured':
        return 'No manufactured orders available';
      case 'tested':
        return 'No tested orders available';
      case 'delivered':
        return 'No delivered orders available';
      default:
        return 'No data available';
    }
  };

  return (
    <motion.div initial="hidden" animate="visible" style={{ perspective: 1000 }}>
      <Box sx={{ width: '95%', mb: { xs: '10%', sm: '0%' }, mt: { xs: '25%', sm: '15%', md: '10%' }, ml: { xs: '5%', sm: '0%', md: '3%' } }}>
        {/* Dashboard Cards for Desktop */}
        {!isMobile && (
          <Grid container spacing={2} sx={{ ml: { xs: 3, sm: '13%', md: '20%' } }}>
            {[
              { title: "Pending", Icon: Pending, color: "#ed6c02" },
              { title: "Progress", Icon: Clock, color: "#9c27b0" },
              { title: "Manufactured", Icon: Factory, color: "#1976d2" },
              { title: "Tested", Icon: AssignmentTurnedInOutlined, color: "#2e7d32" },
              { title: "Delivered", Icon: LocalShipping, color: "#1976d2" }
            ].map((card, index) => (
              <Grid key={index} size={{ xs: 11, md: 4, lg: 2.4 }}>
                <DashboardCard
                  title={card.title}
                  value={getCategoryCount(card.title.toLowerCase())}
                  Icon={card.Icon}
                  color={card.color}
                  delay={0.1 * (index + 1)}
                  onCardClick={handleCardClick}
                  isActive={selectedCard === card.title}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Filter Dropdown for Mobile */}
        {isMobile && (
          <Box sx={{
            width: '90%',
            mx: 'auto',
            mb: 3
          }}>
            <Typography variant="h4" component="h1" sx={{ color: '#343C6A', fontFamily: 'Mont', fontWeight: 'bold', mb: 3 }}>
              Production Tracking
            </Typography>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="production-status-label">Production Status</InputLabel>
              <Select
                labelId="production-status-label"
                id="production-status"
                value={selectedCard.toLowerCase()}
                onChange={handleFilterChange}
                label="Production Status"
                sx={{
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#E6EFF5',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2D60FF',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2D60FF',
                  },
                }}
              >
                <MenuItem value="pending">Pending ({getCategoryCount('pending')})</MenuItem>
                <MenuItem value="progress">Progress ({getCategoryCount('progress')})</MenuItem>
                <MenuItem value="manufactured">Manufactured ({getCategoryCount('manufactured')})</MenuItem>
                <MenuItem value="tested">Tested ({getCategoryCount('tested')})</MenuItem>
                <MenuItem value="delivered">Delivered ({getCategoryCount('delivered')})</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}

        {/* Table Display for Desktop / Card Display for Mobile */}
        {selectedCard && (
          <Box sx={{
            width: { xs: '90%', md: '70%' },
            ml: { xs: '5%', sm: '15%', md: '25%' },
            mt: { xs: '5%', sm: '5%' },
            justifyContent: 'center',
            alignItems: 'center',
          }}>
            {isMobile ? (
              // Mobile Card View
              <Box>
                {currentData.length > 0 ? (
                  currentData.map((item, index) => (
                    <DataCard
                      key={index}
                      item={item}
                      columns={tableColumns}
                    />
                  ))
                ) : (
                  <Card sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                      {getEmptyStateMessage(selectedCard)}
                    </Typography>
                  </Card>
                )}
              </Box>
            ) : (
              // Desktop Table View
              <TableContainer component={Paper} sx={{
                boxShadow: 'none',
                '& .MuiTable-root': {
                  borderCollapse: 'collapse'
                },
                mt: '5%'
              }}>
                <Table sx={{
                  minWidth: 650,
                  '& .MuiTableCell-root': {
                    borderBottom: '1px solid #E6EFF5',
                    borderTop: 'none',
                    borderLeft: 'none',
                    borderRight: 'none',
                    padding: '8px 16px',
                  },
                  '& .MuiTableHead-root .MuiTableCell-root': {
                    borderBottom: '1px solid #E6EFF5',
                    fontWeight: 'bold',
                    color: '#718EBF',
                    fontFamily: 'Inter',
                    padding: '8px 16px',
                  }
                }}>
                  <TableHead>
                    <TableRow>
                      {tableColumns.map((col, index) => (
                        <TableCell key={index} sx={{ fontWeight: 'bold', color: '#718EBF', fontFamily: 'Inter' }}>{col}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentData.length > 0 ? (
                      currentData.map((item, rowIndex) => (
                        <TableRow key={rowIndex}>
                          {tableColumns.map((col, colIndex) => (
                            <TableCell key={colIndex}>
                              {renderCellContent(item, col)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={tableColumns.length} align="center">
                          {getEmptyStateMessage(selectedCard)}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Box>
    </motion.div>
  );
};

export default ProductionTracking;
